/**
 * Inbox API
 *
 * User-facing notification inbox: fetch, mark read, archive, preferences.
 * Uses /notifications/inbox/* and /notifications/preferences endpoints.
 */

import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type {
  GetNotificationsParams,
  GetNotificationsResponse,
  Notification,
  NotificationAction,
  NotificationActionType,
  NotificationCategory,
  NotificationPreferences,
  NotificationPriority,
} from '@wg/types';

interface BackendNotificationItem {
  id: string;
  notification_type: string;
  category: string;
  title: string;
  body: string;
  image_url?: string;
  action_type?: string;
  action_target?: string;
  is_read: boolean;
  priority: string;
  created_at: string;
  deep_link?: string;
}

interface BackendInboxResponse {
  notifications: BackendNotificationItem[];
  unread_count: number;
  total: number;
}

function transformNotification(item: BackendNotificationItem): Notification {
  const action: NotificationAction | undefined =
    item.action_type && item.action_target
      ? {
          type: item.action_type as NotificationActionType,
          label: 'View',
          url: item.action_target,
        }
      : undefined;

  return {
    id: item.id,
    category: item.category as NotificationCategory,
    priority: item.priority as NotificationPriority,
    status: item.is_read ? 'read' : 'unread',
    title: item.title,
    body: item.body,
    deep_link: item.deep_link,
    created_at: item.created_at,
    display: item.image_url ? { image_url: item.image_url } : undefined,
    actions: action ? [action] : undefined,
  };
}

export interface InboxApi {
  getNotifications(
    params?: GetNotificationsParams,
  ): Promise<GetNotificationsResponse>;
  getUnreadCount(): Promise<number>;
  markAsRead(notificationId: string): Promise<void>;
  markAllAsRead(): Promise<void>;
  archiveNotification(notificationId: string): Promise<void>;
  getPreferences(): Promise<NotificationPreferences>;
  updatePreferences(
    preferences: Partial<NotificationPreferences>,
  ): Promise<NotificationPreferences>;
}

export function createInboxApi(http: AxiosInstance, logger: Logger): InboxApi {
  return {
    async getNotifications(params = {}) {
      try {
        logger.debug('[InboxAPI] Fetching notifications', params);

        const queryParams = new URLSearchParams();
        if (params.status && params.status !== 'all')
          queryParams.append('status', params.status);
        if (params.category) queryParams.append('category', params.category);
        if (params.trip_id) queryParams.append('trip_id', params.trip_id);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());

        const queryString = queryParams.toString();
        const url = `/notifications/inbox${queryString ? `?${queryString}` : ''}`;

        const response = await http.get<BackendInboxResponse>(url);
        const notifications = response.data?.notifications ?? [];

        logger.debug('[InboxAPI] Notifications fetched', {
          count: notifications.length,
          total: response.data?.total ?? 0,
          unread: response.data?.unread_count ?? 0,
        });

        return {
          notifications: notifications.map(transformNotification),
          total: response.data?.total ?? 0,
          unread_count: response.data?.unread_count ?? 0,
          has_more: notifications.length === (params.limit || 20),
        };
      } catch (error) {
        logger.error('[InboxAPI] Failed to fetch notifications', error);
        throw error;
      }
    },

    async getUnreadCount() {
      try {
        logger.debug('[InboxAPI] Fetching unread count');
        const response = await http.get<{ unread_count: number }>(
          '/notifications/inbox/count',
        );
        return response.data?.unread_count ?? 0;
      } catch (error) {
        logger.error('[InboxAPI] Failed to fetch unread count', error);
        throw error;
      }
    },

    async markAsRead(notificationId) {
      try {
        logger.debug('[InboxAPI] Marking notification as read', {
          notificationId,
        });
        await http.post(`/notifications/inbox/${notificationId}/read`);
        logger.info('[InboxAPI] Notification marked as read', {
          notificationId,
        });
      } catch (error) {
        logger.error('[InboxAPI] Failed to mark notification as read', error);
        throw error;
      }
    },

    async markAllAsRead() {
      try {
        logger.debug('[InboxAPI] Marking all as read');
        await http.post('/notifications/inbox/read-all');
        logger.info('[InboxAPI] All notifications marked as read');
      } catch (error) {
        logger.error('[InboxAPI] Failed to mark all as read', error);
        throw error;
      }
    },

    async archiveNotification(notificationId) {
      try {
        logger.debug('[InboxAPI] Archiving notification', { notificationId });
        await http.delete(`/notifications/inbox/${notificationId}`);
        logger.info('[InboxAPI] Notification archived', { notificationId });
      } catch (error) {
        logger.error('[InboxAPI] Failed to archive notification', error);
        throw error;
      }
    },

    async getPreferences() {
      try {
        logger.debug('[InboxAPI] Fetching notification preferences');
        const response = await http.get<NotificationPreferences>(
          '/notifications/preferences',
        );
        return response.data;
      } catch (error) {
        logger.error('[InboxAPI] Failed to fetch preferences', error);
        throw error;
      }
    },

    async updatePreferences(preferences) {
      try {
        logger.debug(
          '[InboxAPI] Updating notification preferences',
          preferences,
        );
        const response = await http.put<NotificationPreferences>(
          '/notifications/preferences',
          preferences,
        );
        logger.info('[InboxAPI] Preferences updated');
        return response.data;
      } catch (error) {
        logger.error('[InboxAPI] Failed to update preferences', error);
        throw error;
      }
    },
  };
}
